import { Link } from "react-router-dom";
import {
  Activity,
  BookOpen,
  BrainCircuit,
  Database,
  DatabaseBackup,
  FlaskConical,
  HeartPulse,
  LineChart,
  Package,
  Pill,
  ShieldAlert,
  SlidersHorizontal,
  Target,
  Upload,
  Zap,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type ToolLink = {
  to: string;
  title: string;
  description: string;
  icon: typeof Pill;
  prominent?: boolean;
  badge?: string;
};

const PRIMARY_TOOLS: ToolLink[] = [
  {
    to: "/medicamentos",
    title: "Meu tratamento",
    description: "Esquema, formulações, horários, resposta individual e histórico.",
    icon: Pill,
  },
  {
    to: "/estoque-medicamentos",
    title: "Estoque",
    description: "Lotes, quantidade disponível e acompanhamento do que está acabando.",
    icon: Package,
  },
  {
    to: "/interacoes",
    title: "Interações",
    description: "Revisão de combinações, mecanismos e sobreposição farmacológica.",
    icon: ShieldAlert,
    prominent: true,
  },
  {
    to: "/biblioteca",
    title: "Biblioteca farmacológica",
    description: "Medicamentos, substâncias, aliases, PK/PD, riscos e fontes.",
    icon: BookOpen,
  },
  {
    to: "/backup",
    title: "Backup e restauração",
    description: "Exportação versionada, checksum, prévia, deduplicação e restauração segura.",
    icon: DatabaseBackup,
  },
  {
    to: "/importar",
    title: "Importar dados externos",
    description: "PsyLog, Journal, DrugsPro, CSV e lotes de importação auditáveis.",
    icon: Database,
  },
];

const CALCULATORS: ToolLink[] = [
  {
    to: "/laboratorio-pkpd",
    title: "Laboratório PK/PD",
    description: "Curvas, formulações, fases, meia-vida e parâmetros editáveis.",
    icon: FlaskConical,
    badge: "PK/PD",
  },
  {
    to: "/curvas",
    title: "Curvas e sessões",
    description: "Timeline farmacológica com doses, sobreposição e evolução temporal.",
    icon: LineChart,
  },
  {
    to: "/doses",
    title: "Registros de dose",
    description: "Adicionar, revisar e contextualizar ingestões sem perder precisão temporal.",
    icon: Activity,
  },
  {
    to: "/sintomas",
    title: "Sintomas-alvo",
    description: "Acompanhar resposta, baseline, metas e medidas repetidas.",
    icon: Target,
  },
  {
    to: "/inteligencia-clinica",
    title: "Inteligência clínica",
    description: "Integra histórico, texto, redose, sintomas, contexto e hipóteses revisáveis.",
    icon: BrainCircuit,
  },
  {
    to: "/meu-espaco",
    title: "Check-in e estado atual",
    description: "Humor, energia, ansiedade, foco, sono, sinais vitais e carga corporal.",
    icon: HeartPulse,
  },
  {
    to: "/base-farmacologica/workbench",
    title: "Workbench farmacológico",
    description: "Edição avançada e inspeção técnica da base farmacológica.",
    icon: SlidersHorizontal,
  },
  {
    to: "/conhecimento-farmacologico",
    title: "Base de conhecimento",
    description: "Fontes estruturadas, importação de conhecimento e revisão de canônicos.",
    icon: Upload,
  },
];


const WORKFLOWS = [
  {
    eyebrow: "01 · RECONSTRUÇÃO TEMPORAL",
    title: "O que aconteceu — e quando?",
    description: "Cruze horários de uso, doses registradas, sono, sintomas e sinais observados. Primeiro organize a sequência; depois formule a hipótese.",
    steps: ["Linha temporal", "Doses", "Sintomas"],
    links: [{ to: "/curvas", label: "Abrir timeline" }, { to: "/doses", label: "Revisar doses" }],
    icon: LineChart,
  },
  {
    eyebrow: "02 · REVISÃO DE SEGURANÇA",
    title: "O que pode estar interagindo?",
    description: "Revise combinações, mecanismos plausíveis, sobreposição de efeitos e lacunas de informação antes de interpretar a resposta clínica.",
    steps: ["Combinações", "Mecanismos", "Incertezas"],
    links: [{ to: "/interacoes", label: "Revisar interações" }, { to: "/biblioteca", label: "Consultar base" }],
    icon: ShieldAlert,
  },
  {
    eyebrow: "03 · HIPÓTESE → EVIDÊNCIA",
    title: "Transforme observações em uma investigação",
    description: "Registre sintomas-alvo, compare com o estado basal e procure explicações concorrentes. Associação temporal não prova causalidade.",
    steps: ["Baseline", "Mudança", "Explicações alternativas"],
    links: [{ to: "/sintomas", label: "Acompanhar sintomas" }, { to: "/inteligencia-clinica", label: "Abrir inteligência" }],
    icon: BrainCircuit,
  },
];

function PrimaryRow({ tool }: { tool: ToolLink }) {
  const Icon = tool.icon;
  return (
    <Link to={tool.to} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-3xl">
      <Card className={cn(
        "rounded-3xl p-4 transition-colors hover:bg-muted/45 active:bg-muted/60",
        tool.prominent && "border-primary/25 bg-primary/[0.035]",
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            "grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-muted text-muted-foreground",
            tool.prominent && "bg-primary/10 text-primary",
          )}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold">{tool.title}</h2>
              {tool.badge ? <Badge variant="secondary" className="text-[10px]">{tool.badge}</Badge> : null}
            </div>
            <p className="mt-0.5 text-sm leading-snug text-muted-foreground">{tool.description}</p>
          </div>
          <Zap className="h-4 w-4 shrink-0 text-muted-foreground/45" aria-hidden="true" />
        </div>
      </Card>
    </Link>
  );
}

function ToolTile({ tool }: { tool: ToolLink }) {
  const Icon = tool.icon;
  return (
    <Link to={tool.to} className="block min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-3xl">
      <Card className="h-full rounded-3xl p-4 transition-colors hover:bg-muted/45 active:bg-muted/60">
        <div className="flex items-start justify-between gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
          {tool.badge ? <Badge variant="outline" className="text-[9px]">{tool.badge}</Badge> : null}
        </div>
        <h2 className="mt-4 font-semibold leading-tight">{tool.title}</h2>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tool.description}</p>
      </Card>
    </Link>
  );
}

export default function ToolsHub() {
  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 pb-4">
      <header className="space-y-1 px-1 pt-1">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">ClinicalOS</div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Ferramentas</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">
          Acesso rápido aos motores clínicos e farmacológicos. Todos usam a mesma base; esta tela apenas organiza os recursos.
        </p>
      </header>

      <Card className="relative overflow-hidden rounded-3xl border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-5 sm:p-6">
        <div className="relative grid gap-5 lg:grid-cols-2 lg:items-center">
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2"><Badge variant="secondary">PK/PD WORKSPACE</Badge><Badge variant="outline">Dados + modelo</Badge></div>
            <div><h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Farmacologia no tempo</h2><p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">Explore curvas estimadas, sobreposição de substâncias e observações clínicas. Diferencie o que foi medido, relatado e modelado.</p></div>
            <div className="flex flex-wrap gap-2"><Link to="/laboratorio-pkpd" className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-medium text-primary-foreground"><FlaskConical className="h-4 w-4" /> Abrir laboratório</Link><Link to="/curvas" className="inline-flex min-h-11 items-center gap-2 rounded-xl border bg-background/70 px-4 text-sm font-medium"><LineChart className="h-4 w-4" /> Ver linha temporal</Link></div>
          </div>
          <div className="rounded-2xl border bg-background/70 p-3 sm:p-4"><div className="mb-3 flex items-center justify-between"><div><div className="text-sm font-medium">Curva conceitual</div><div className="text-xs text-muted-foreground">Intensidade relativa · não é concentração plasmática</div></div><Badge variant="outline">PK</Badge></div>
            <div className="clinical-grid rounded-xl border p-3"><svg viewBox="0 0 320 110" role="img" aria-label="Curva farmacocinética conceitual" className="h-auto w-full"><path d="M8 94 C30 94 38 88 54 62 S88 12 115 18 S151 47 177 63 S232 84 312 94" fill="none" stroke="hsl(var(--info))" strokeWidth="3" strokeLinecap="round"/><path d="M8 94 C38 94 56 86 78 68 S117 43 145 50 S208 80 312 94" fill="none" stroke="hsl(var(--success))" strokeWidth="2.5" strokeDasharray="5 5" strokeLinecap="round"/><path d="M160 8V98" stroke="currentColor" strokeOpacity=".35" strokeDasharray="3 4"/><circle cx="160" cy="53" r="4" fill="hsl(var(--info))"/></svg></div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground"><span>● Curva estimada</span><span>● Trajetória ilustrativa</span></div><p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">Visual conceitual, sem representar uma substância, dose ou previsão clínica real.</p>
          </div>
        </div>
      </Card>

      <section className="space-y-3">
        <div className="px-1 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Acesso clínico</div>
        {PRIMARY_TOOLS.map((tool) => <PrimaryRow key={tool.to} tool={tool} />)}
      </section>

      <section className="space-y-3">
        <div className="px-1 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Análise e cálculo</div>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {CALCULATORS.map((tool) => <ToolTile key={tool.to} tool={tool} />)}
        </div>
      </section>

      <Card className="rounded-3xl border-dashed p-4 text-sm text-muted-foreground">
        As estimativas farmacocinéticas e farmacodinâmicas são modelos. Quando faltam parâmetros estruturados, o sistema deve mostrar incerteza em vez de fabricar precisão.
      </Card>
    </div>
  );
}
